import { useState } from "react";
import { Globe } from "lucide-react";

/**
 * Reusable translations panel for admin forms.
 * English is the primary language (edited in the main form).
 * This panel provides fields for JA, NL, FR overrides.
 * Empty fields gracefully fall back to the English value.
 */

export type TranslatableLang = "ja" | "nl" | "fr";

export type I18nMap = Partial<Record<TranslatableLang, Record<string, string>>>;

const TRANSLATABLE_LANGS: { code: TranslatableLang; label: string; short: string }[] = [
  { code: "ja", label: "日本語", short: "JP" },
  { code: "nl", label: "Nederlands", short: "NL" },
  { code: "fr", label: "Français", short: "FR" },
];

export interface TranslationField {
  key: string;
  label: string;
  type?: "input" | "textarea";
  rows?: number;
  englishValue?: string;
}

/**
 * Resolves a localized value from an entity with an i18n map.
 * Falls back to the English (default) value when no translation exists.
 */
export function localized(
  entity: Record<string, any> & { i18n?: I18nMap },
  field: string,
  lang: string
): string {
  if (lang === "en" || !entity.i18n) return entity[field] || "";
  return entity.i18n[lang as TranslatableLang]?.[field] || entity[field] || "";
}

export function TranslationsPanel({
  fields,
  i18n,
  onChange,
}: {
  fields: TranslationField[];
  i18n: I18nMap;
  onChange: (i18n: I18nMap) => void;
}) {
  const [activeLang, setActiveLang] = useState<TranslatableLang>("ja");

  const getValue = (field: string) => i18n[activeLang]?.[field] || "";

  const setValue = (field: string, value: string) => {
    onChange({
      ...i18n,
      [activeLang]: { ...(i18n[activeLang] || {}), [field]: value },
    });
  };

  const filledCount = (lang: TranslatableLang) =>
    fields.filter((f) => (i18n[lang]?.[f.key] || "").trim()).length;

  return (
    <div className="border border-[#E6E0D7] rounded-xl overflow-hidden">
      {/* Header with language tabs */}
      <div className="flex items-center gap-1 px-4 py-3 bg-[#F7F5F1] border-b border-[#E6E0D7]">
        <Globe size={14} className="text-[#6F675F] mr-1.5 shrink-0" />
        <span
          className="text-[#6F675F] tracking-[0.1em] uppercase mr-3 shrink-0"
          style={{ fontFamily: "'Jost', sans-serif", fontSize: "10px" }}
        >
          Translations
        </span>
        <div className="flex gap-1">
          {TRANSLATABLE_LANGS.map((l) => (
            <button
              key={l.code}
              onClick={() => setActiveLang(l.code)}
              className={`px-3 py-1.5 rounded-lg tracking-[0.08em] transition-all duration-200 ${
                activeLang === l.code
                  ? "bg-white text-[#2D2A26] shadow-[0_1px_4px_rgba(40,32,20,0.08)]"
                  : "text-[#6F675F] hover:text-[#2D2A26]"
              }`}
              style={{ fontFamily: "'Jost', sans-serif", fontSize: "11px" }}
            >
              {l.short}
              {filledCount(l.code) > 0 && (
                <span className="ml-1 text-[#6F7F63]" style={{ fontSize: "9px" }}>
                  {filledCount(l.code)}/{fields.length}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Active language label */}
      <div className="px-4 pt-4 pb-1 flex items-center justify-between">
        <span className="text-[#2D2A26]" style={{ fontFamily: "'Jost', sans-serif", fontSize: "12px" }}>
          {TRANSLATABLE_LANGS.find((l) => l.code === activeLang)?.label}
        </span>
        <span className="text-[#D8D1C7]" style={{ fontSize: "10px" }}>
          Leave empty to use English default
        </span>
      </div>

      {/* Translation fields */}
      <div className="px-4 pb-4 pt-2 space-y-3">
        {fields.map((f) => (
          <div key={f.key}>
            <label
              className="block text-[#6F675F] tracking-[0.12em] uppercase mb-1.5"
              style={{ fontSize: "10px", fontWeight: 400 }}
            >
              {f.label}
            </label>
            {f.type === "textarea" ? (
              <textarea
                rows={f.rows || 3}
                value={getValue(f.key)}
                onChange={(e) => setValue(f.key, e.target.value)}
                placeholder={f.englishValue || ""}
                className="w-full px-3 py-2.5 bg-[#F7F5F1] border border-[#E6E0D7] rounded-lg text-[#2D2A26] placeholder:text-[#D8D1C7]/60 focus:outline-none focus:border-[#C7A76C] transition-colors resize-none"
                style={{ fontSize: "13px" }}
              />
            ) : (
              <input
                value={getValue(f.key)}
                onChange={(e) => setValue(f.key, e.target.value)}
                placeholder={f.englishValue || ""}
                className="w-full px-3 py-2.5 bg-[#F7F5F1] border border-[#E6E0D7] rounded-lg text-[#2D2A26] placeholder:text-[#D8D1C7]/60 focus:outline-none focus:border-[#C7A76C] transition-colors"
                style={{ fontSize: "13px" }}
              />
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
